import React from 'react'
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  DragOverlay,
} from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'

/**
 * PlannerDnd — wraps the planner grid with drag-and-drop support.
 * Drag a MealCard onto another slot (empty or filled) to move/swap.
 *
 * Slot ID convention: `slot:<dayOfWeek>:<mealType>`
 * Draggable ID convention: `meal:<slotId>`
 */
export function PlannerDnd({ children, onDrop }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    // Long-press required on touch to avoid conflict with scroll.
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
  )
  const [active, setActive] = React.useState(null)

  function handleDragStart(event) {
    setActive(event.active)
  }

  function handleDragEnd(event) {
    setActive(null)
    const { active: a, over } = event
    if (!over) return
    const fromId = parseDraggableId(a.id)
    const toTarget = parseDroppableId(over.id)
    if (!fromId || !toTarget) return
    if (fromId.dayOfWeek === toTarget.dayOfWeek && fromId.mealType === toTarget.mealType) return
    onDrop?.({ from: fromId, to: toTarget })
  }

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      {children}
      <DragOverlay dropAnimation={{ duration: 150 }}>
        {active ? (
          <div className="bg-white rounded-lg border-l-4 border-l-emerald-500 shadow-lg p-3 opacity-90">
            <p className="font-medium text-gray-800 text-sm">
              {active.data?.current?.label ?? 'Repas'}
            </p>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

export function DroppableSlot({ dayOfWeek, mealType, children, className = '' }) {
  const id = `slot:${dayOfWeek}:${mealType}`
  const { setNodeRef, isOver } = useDroppable({ id })
  return (
    <div
      ref={setNodeRef}
      className={`${className} transition-colors ${isOver ? 'ring-2 ring-emerald-400 ring-offset-1 rounded-lg' : ''}`}
    >
      {children}
    </div>
  )
}

export function DraggableMeal({ slot, label, children }) {
  const id = `meal:${slot.day_of_week}:${slot.meal_type}`
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    data: { label },
  })
  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.4 : 1,
    cursor: 'grab',
    touchAction: 'none', // required so TouchSensor can take over
  }
  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      {children}
    </div>
  )
}

function parseDraggableId(id) {
  const m = String(id).match(/^meal:(\d+):(\w+)$/)
  if (!m) return null
  return { dayOfWeek: parseInt(m[1], 10), mealType: m[2] }
}

function parseDroppableId(id) {
  const m = String(id).match(/^slot:(\d+):(\w+)$/)
  if (!m) return null
  return { dayOfWeek: parseInt(m[1], 10), mealType: m[2] }
}
